<template>
  <div class="step-component">
    <div class="step-component-header">
      <h1 v-if="isTitleObject" class="step-title">
        <span>{{ title.base }}</span>
        <span :class="title.class">{{ title.highlight }}</span>
        <span>{{ title.tail || '' }}</span>
      </h1>
      <h1 v-else class="step-title">{{ title }}</h1>
      <template v-if="!isHideStepProgress">
        <div class="step-number-holder">
          <p v-for="(_, index) in 3" :key="index" class="step-number">
            <img :src="getStepIcon(index + 1)" :alt="'step-' + (index + 1)">
            <span>{{ getStepText(index + 1) }}</span>
          </p>
        </div>
      </template>
      <h4 v-if="isSubTitleObject" class="step-title">
        <span>{{ subtitle.base }}</span>
        <span :class="subtitle.class">{{ subtitle.highlight }}</span>
        <span> {{ subtitle.tail || '' }}</span>
      </h4>
      <h4 v-else class="step-title">{{ subtitle }}</h4>
    </div>
    <div class="step-content">
      <slot/>
      <SwiperComponent v-if="isOptionsExist" :options="options" :currentStep="currentStep" :active-item="currentStep.activeItem" @select-item="handleSelect" />
      <ActionButtons v-if="showActions" @prev-step="prevStep" @next-step="nextStep" />
    </div>
  </div>
</template>

<script>
import { computed, watch } from "vue";
import SwiperComponent from "./SwiperComponent.vue";
import ActionButtons from "./ActionButtons.vue";
import ActiveStep from "@/assets/icons/step-active.svg";
import HalfStep from "@/assets/icons/step-half.svg";
import FullStep from "@/assets/icons/step-full.svg";

export default {
  name: "StepComponent",
  components: { SwiperComponent, ActionButtons },
  props: {
    step: { type: Number, required: true },
    title: { type: String, required: true },
    subtitle: { type: String, required: true },
    options: { type: Array, default: () => [] },
    showActions: { type: Boolean, default: true },
    isFinal: { type: Boolean, default: false },
    currentStep: { type: Object, default: {} },
  },
  emits: ["prev-step", "next-step", "select-item", "generate"],
  setup(props, { emit }) {
    const getStepIcon = (step) => {
      if (step === 1 && props.currentStep.isFinal) return ActiveStep
      if (step === 2 && props.currentStep.prevStep) return ActiveStep
      if (step === 2) return HalfStep
      return FullStep;
    };

    const getStepText = (step) => ["Let's Get Started", "Select a format", "Finish"][step - 1];
    const handleSelect = (id) => emit("select-item", id);
    const prevStep = () => emit("prev-step");
    const nextStep = () => emit("next-step");

    watch(() => props.isFinal, (isFinal) => {
      if (isFinal) {
        emit("generate");
      }
    }, { immediate: true });

    const isOptionsExist = computed(() => {
      return Array.isArray(props.options) && props.options.length > 0;
    });

    const isTitleObject = computed(() =>
        typeof props.title === 'object' && props.title.base && props.title.highlight
    );

    const isSubTitleObject = computed(() =>
        typeof props.subtitle === 'object' && props.subtitle.base && props.subtitle.highlight
    );

    const isHideStepProgress = computed(() => {
      return Object.hasOwn(props.currentStep, 'isHideStepProgress') && props.currentStep.isHideStepProgress;
    });

    return {
      getStepIcon,
      getStepText,
      handleSelect,
      prevStep,
      nextStep,
      isOptionsExist,
      isTitleObject,
      isSubTitleObject,
      isHideStepProgress
    };
  },
};
</script>

<style lang="scss" scoped>
.step {
  &-title {
    font-size: 40px;
    font-weight: 700;
    text-align: center;
    margin-bottom: 20px;
  }
}
.step-component {
  height: 100%;
  padding-bottom: 70px;

  &-header {
    text-align: center;
    margin-bottom: 15px;

    .step-title {
      color: #fff;
      font-size: 40px;
      font-weight: 700;
      margin-bottom: 20px;

      .green-text { color: #ACF601; }

      @media (max-width: 768px) {
        font-size: 25px;
        margin-bottom: 10px;
      }
      @media (max-width: 500px) {
        margin-top: 10px;
      }
      @media (max-width: 435px) {
        font-size: 22px;
        margin-bottom: 5px;
      }
    }

    .step-number-holder {
      display: flex;
      justify-content: center;
      gap: 64px;
      margin-bottom: 15px;

      @media (max-width: 768px) { gap: 40px; }
      @media (max-width: 460px) {
        gap: 15px;
        margin-top: 20px;
      }

      .step-number {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 75px;
        gap: 8px;

        img { width: 75px; height: 75px; }

        @media (max-width: 460px) {
          img { width: 40px; height: 40px; }
          span { font-size: 12px; }
        }
      }
    }

    &-subtitle {
      color: #fff;
      font-size: 40px;
      text-transform: capitalize;
      margin-bottom: 30px;

      @media (max-width: 768px) {
        font-size: 30px;
        margin-bottom: 10px;
      }
    }
  }

  &-content { position: relative; }
}
</style>